/* eslint-disable max-len */
/**
 * @file ext-overview_window.js
 *
 * @license MIT
 *
 * @copyright 2013 James Sacksteder
 *
 */

/* DragMove */
// https://github.com/knadh/dragmove.js
// Kailash Nadh (c) 2020.
// MIT License.
// can't use npm version as the dragmove is different.

let _loaded = false;
let _callbacks = [];
const _isTouch = window.ontouchstart !== undefined;

const dragmove = function (target, handler, parent, onStart, onEnd, onDrag) {
    // Register a global event to capture mouse moves (once).
    if (!_loaded) {
        document.addEventListener(_isTouch ? "touchmove" : "mousemove", function (e) {
            let c = e;
            if (e.touches) {
                c = e.touches[0];
            }

            // On mouse move, dispatch the coords to all registered callbacks.
            for (const callback of _callbacks) {
                callback(c.clientX, c.clientY);
            }
        });
    }

    _loaded = true;
    let isMoving = false, hasStarted = false;
    let startX = 0, startY = 0, lastX = 0, lastY = 0;

    // On the first click and hold, record the offset of the pointer in relation
    // to the point of click inside the element.
    handler.addEventListener(_isTouch ? "touchstart" : "mousedown", function (e) {
        e.stopPropagation();
        e.preventDefault();
        if (target.dataset.dragEnabled === "false") {
            return;
        }

        let c = e;
        if (e.touches) {
            c = e.touches[0];
        }

        isMoving = true;
        startX = target.offsetLeft - c.clientX;
        startY = target.offsetTop - c.clientY;
    });

    // On leaving click, stop moving.
    document.addEventListener(_isTouch ? "touchend" : "mouseup", function () {
        try {
            if (onEnd && hasStarted) {
                onEnd(target, parent, parseInt(target.style.left), parseInt(target.style.top));
            }
        } finally {
            // Always clear the drag state, even if onEnd throws, so the view box can't get
            // stuck following the cursor after the mouse button has been released.
            isMoving = false;
            hasStarted = false;
        }
    });

    // On leaving click, stop moving.
    document.addEventListener(_isTouch ? "touchmove" : "mousemove", function () {
        if (onDrag && hasStarted) {
            onDrag(target, parseInt(target.style.left), parseInt(target.style.top));
        }
    });

    // Register mouse-move callback to move the element.
    _callbacks.push(function move(x, y) {
        if (!isMoving) {
            return;
        }

        if (!hasStarted) {
            hasStarted = true;
            if (onStart) {
                onStart(target, lastX, lastY);
            }
        }

        lastX = x + startX;
        lastY = y + startY;

        // If boundary checking is on, don't let the element cross the viewport.
        if (target.dataset.dragBoundary === "true") {
            if (lastX < 1 || lastX >= window.innerWidth - target.offsetWidth) {
                return;
            }
            if (lastY < 1 || lastY >= window.innerHeight - target.offsetHeight) {
                return;
            }
        }

        target.style.left = lastX + "px";
        target.style.top = lastY + "px";
    });
}
/* End DragMove */

export default {
    name: 'overview_window',
    init({ _$, isChrome }) {
        const svgEditor = this;
        const { $id } = svgEditor.svgCanvas;
        const overviewWindowGlobals = {};
        // Disabled in Chrome 48-, see https://github.com/SVG-Edit/svgedit/issues/26 and
        // https://code.google.com/p/chromium/issues/detail?id=565120.
        if (isChrome()) {
            const verIndex = navigator.userAgent.indexOf('Chrome/') + 7;
            const chromeVersion = Number.parseInt(navigator.userAgent.substring(verIndex));
            if (chromeVersion < 49) {
                return undefined;
            }
        }

        // Define and insert the base html element.
        const propsWindowHtml = `
<style>
#overview_window_content_pane {
    word-wrap: break-word;
    display: inline-block;
    position: fixed;
    left: 60px;
    bottom: 70px;
    width: 150px;
    z-index: 1;
}

#overview_window_content {
    position: relative;
    left: 12px;
    top: 0px;
}

#overview_window_content > div {
    background-color: #73797f;
    background-color: rgba(115,121,127,0.85);
    display: inline-block; 
    overflow: visible;
}

#overview_window_view_box {
    min-width: 50px; 
    min-height: 50px;
    position: absolute;
    top: 30px;
    left: 30px; 
    z-index: 5;
    background-color: rgba(255,0,102,0.3);
}
</style>
<div id="overview_window_content_pane">
    <div id="overview_window_content">
        <div>
            <svg id="overviewMiniView" width="150" height="100" x="0" 
                y="0" viewBox="0 0 4800 3600" 
                xmlns="http://www.w3.org/2000/svg" 
                xmlns:xlink="http://www.w3.org/1999/xlink">
                <use x="0" y="0" xlink:href="#svgroot"> </use>
            </svg>
            <div id="overview_window_view_box">
            </div>
        </div>
    </div>
</div>
`;
        $id("tools_right").insertAdjacentHTML('beforeend', propsWindowHtml);

        // Define dynamic animation of the view box.
        const updateViewBox = function () {
            const warea = $id('workarea');
            const workareaStyle = getComputedStyle(warea, null);

            const portHeight = parseFloat(workareaStyle.height.replace("px", ""));
            const portWidth = parseFloat(workareaStyle.width.replace("px", ""));
            const portX = warea.scrollLeft;
            const portY = warea.scrollTop;

            const svgcanvasStyle = getComputedStyle($id("svgcanvas"), null);
            const overviewMiniViewStyle = getComputedStyle($id("overviewMiniView"), null);

            const windowWidth = parseFloat(svgcanvasStyle.width.replace("px", ""));
            const windowHeight = parseFloat(svgcanvasStyle.height.replace("px", ""));
            const overviewWidth = parseFloat(overviewMiniViewStyle.width.replace("px", ""));
            const overviewHeight = parseFloat(overviewMiniViewStyle.height.replace("px", ""));

            const viewBoxX = portX / windowWidth * overviewWidth;
            const viewBoxY = portY / windowHeight * overviewHeight;
            const viewBoxWidth = portWidth / windowWidth * overviewWidth;
            const viewBoxHeight = portHeight / windowHeight * overviewHeight;

            const viewBoxEl = $id("overview_window_view_box");

            viewBoxEl.style.minWidth = viewBoxWidth + 'px';
            viewBoxEl.style.minHeight = viewBoxHeight + 'px';
            viewBoxEl.style.top = viewBoxY + 'px';
            viewBoxEl.style.left = viewBoxX + 'px';
        };
        $id('workarea').addEventListener('scroll', () => {
            if (!(overviewWindowGlobals.viewBoxDragging)) {
                updateViewBox();
            }
        });
        $id('workarea').addEventListener('resize', updateViewBox);
        updateViewBox();

        // Compensate for changes in zoom and canvas size.
        const updateViewDimensions = function () {
            const svgRootEl = $id("svgroot");
            const svgRootStyle = getComputedStyle(svgRootEl, null)

            const viewWidth = parseFloat(svgRootStyle.width.replace("px", ""));
            const viewHeight = parseFloat(svgRootStyle.height.replace("px", ""));

            const viewX = 640;
            const viewY = 480;

            const overviewMiniViewEl = $id("overviewMiniView");

            const svgWidthOld = parseFloat(getComputedStyle(overviewMiniViewEl, null).width.replace("px", ""));
            const svgHeightNew = Math.min(viewHeight / viewWidth * svgWidthOld, window.innerHeight * 0.6);
            overviewMiniViewEl.setAttribute('viewBox', viewX + ' ' + viewY + ' ' + viewWidth + ' ' + viewHeight);
            overviewMiniViewEl.setAttribute('height', svgHeightNew);
            updateViewBox();
        };
        updateViewDimensions();

        // Set up the overview window as a controller for the view port.
        overviewWindowGlobals.viewBoxDragging = false;
        const updateViewPortFromViewBox = function () {
            const svgcanvasStyle = getComputedStyle($id("svgcanvas"), null);
            const overviewMiniViewStyle = getComputedStyle($id("overviewMiniView"), null);
            const viewBoxStyle = getComputedStyle($id("overview_window_view_box"), null);

            const windowWidth = parseFloat(svgcanvasStyle.width.replace("px", ""));
            const windowHeight = parseFloat(svgcanvasStyle.height.replace("px", ""));
            const overviewWidth = parseFloat(overviewMiniViewStyle.width.replace("px", ""));
            const overviewHeight = parseFloat(overviewMiniViewStyle.height.replace("px", ""));
            const viewBoxX = parseFloat(viewBoxStyle.getPropertyValue('left').replace("px", ""));
            const viewBoxY = parseFloat(viewBoxStyle.getPropertyValue('top').replace("px", ""));

            const workareaEl = $id("workarea");
            const portX = viewBoxX / overviewWidth * windowWidth;
            const portY = viewBoxY / overviewHeight * windowHeight;
            workareaEl.scrollLeft = portX;
            workareaEl.scrollTop = portY;
        };
        const onStart = () => {
            overviewWindowGlobals.viewBoxDragging = true;
            updateViewPortFromViewBox();
        };
        const onEnd = (el, parent, _x, _y) => {
            if ((el.offsetLeft + el.offsetWidth) > parseFloat(getComputedStyle(parent, null).width.replace("px", ""))) {
                el.style.left = (parseFloat(getComputedStyle(parent, null).width.replace("px", "")) - el.offsetWidth) + 'px';
            } else if (el.offsetLeft < 0) {
                el.style.left = "0px"
            }
            if ((el.offsetTop + el.offsetHeight) > parseFloat(getComputedStyle(parent, null).height.replace("px", ""))) {
                el.style.top = (parseFloat(getComputedStyle(parent, null).height.replace("px", "")) - el.offsetHeight) + 'px';
            } else if (el.offsetTop < 0) {
                el.style.top = "0px"
            }
            overviewWindowGlobals.viewBoxDragging = false;
            updateViewPortFromViewBox();
        };
        const onDrag = function () {
            updateViewPortFromViewBox();
        };

        const dragElem = $id("overview_window_view_box");
        const parentElem = $id("overviewMiniView");
        const overviewMiniViewEl = $id("overviewMiniView");

        dragmove(dragElem, dragElem, parentElem, onStart, onEnd, onDrag);

        overviewMiniViewEl.addEventListener("click", evt => {
            const overviewWindowViewBoxEl = $id("overview_window_view_box");

            const overviewMiniViewStyle = getComputedStyle(overviewMiniViewEl, null)
            const overviewWindowViewBoxStyle = getComputedStyle(overviewWindowViewBoxEl, null)

            // Firefox doesn't support evt.offsetX and evt.offsetY.
            const mouseX = (evt.offsetX || evt.originalEvent.layerX);
            const mouseY = (evt.offsetY || evt.originalEvent.layerY);
            const overviewWidth = parseFloat(overviewMiniViewStyle.width.replace("px", ""));
            const overviewHeight = parseFloat(overviewMiniViewStyle.height.replace("px", ""));
            const viewBoxWidth = parseFloat(overviewWindowViewBoxStyle.getPropertyValue('min-width').replace("px", ""));
            const viewBoxHeight = parseFloat(overviewWindowViewBoxStyle.getPropertyValue('min-height').replace("px", ""));

            let viewBoxX = mouseX - 0.5 * viewBoxWidth;
            let viewBoxY = mouseY - 0.5 * viewBoxHeight;
            // deal with constraints
            if (viewBoxX < 0) {
                viewBoxX = 0;
            }
            if (viewBoxY < 0) {
                viewBoxY = 0;
            }
            if (viewBoxX + viewBoxWidth > overviewWidth) {
                viewBoxX = overviewWidth - viewBoxWidth;
            }
            if (viewBoxY + viewBoxHeight > overviewHeight) {
                viewBoxY = overviewHeight - viewBoxHeight;
            }

            overviewWindowViewBoxEl.style.top = viewBoxY + 'px';
            overviewWindowViewBoxEl.style.left = viewBoxX + 'px';
            updateViewPortFromViewBox();
        });

        return {
            name: 'overview window',
            canvasUpdated: updateViewDimensions,
            workareaResized: updateViewBox
        };
    }
};
